import { TextField, Typography } from "@material-ui/core";
import { Add as AddIcon, Close as CloseIcon } from "@material-ui/icons";
import { Stack } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import _ from "lodash";
import React, { isValidElement, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { editUserProfileAction } from "../../redux/actions";
import { useUserUpdateProfile } from "../../services/mutations";
import { handleFileUpload } from "../../utils/fileUpload";
import Button from "../Button";
import CropperProfilePhoto from "../CropperProfilePhoto/CropperProfilePhoto";
import AddLinksForm from "./AddLinksForm";
import { hasAllAlphabets, isValidURL } from "../../utils/helpers";
import { ASSETS } from "../../utils/config";

const EditProfileCard = ({ userProfile, onBackClick }) => {
  const dispatch = useDispatch();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [web, setWeb] = useState("");
  const [occupation, setOccupation] = useState("");
  const [description, setDescription] = useState("");
  const [profileImg, setProfileImg] = useState("");
  const [areLinksValid, setAreLinksValid] = useState(false);
  const [editImage, setEditImage] = useState(false);
  const [profileSocialMediaLinks, setProfileSocialMediaLinks] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFirstName(userProfile?.first_name);
    setLastName(userProfile?.last_name);
    setOccupation(userProfile?.profession);
    setDescription(userProfile?.bio);
    setProfileImg(
      userProfile?.profile_image
        ? userProfile?.profile_image
        : ASSETS.userPlaceholder
    );
    setWeb(userProfile?.website_url);
  }, [userProfile]);

  // To convert our  API format into our state
  function convertToSocialLinks(socialMediaLinksObj) {
    const socialMediaLinks = socialMediaLinksObj;
    let socialLinks = [];
    for (const platform in socialMediaLinks) {
      let formattedPlatform =
        platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();

      socialLinks.push({
        platform: formattedPlatform,
        link: socialMediaLinks[platform],
      });
    }
    return socialLinks;
  }

  function convertSocialLinks(socialLinks) {
    let socialMediaLinks = {};

    socialLinks.forEach((value) => {
      const { link, platform } = value;
      let formattedLink = link;
      if (platform && link) {
        if (link.startsWith("www")) {
          formattedLink = link.replace("www.", "https://");
        }

        let formattedPlatform = platform.toLowerCase();
        socialMediaLinks[formattedPlatform] = formattedLink;
      }
    });

    return { social_media_links: socialMediaLinks };
  }

  const userEdit = useUserUpdateProfile();

  const [isLoading, setIsLoading] = useState(false);

  var hasFirstNameChanged =
    userProfile?.first_name !== firstName && firstName !== "";
  var hasLastNameChanged = userProfile?.last_name !== lastName;
  var hasProfessionChanged =
    userProfile?.profession !== occupation && occupation !== "";
  var hasBioChanged = userProfile?.bio !== description && description !== "";
  var hasImageChanged = userProfile?.profile_image !== profileImg;
  var hasWebChanged = userProfile?.website_url !== web;
  var hasSocialLinksChanged = _.isEqual(
    userProfile.social_media_links,
    convertSocialLinks(profileSocialMediaLinks).social_media_links
  );

  const validateData = () => {
    if (!firstName) {
      return setErrors({ ...errors, name: "Please enter first name" });
    }
    if (
      !hasAllAlphabets(firstName.trim()) ||
      (lastName && !hasAllAlphabets(lastName.trim()))
    ) {
      return setErrors({ ...errors, name: "Name should only contain letters" });
    }
    if (web && !isValidURL(web))
      return setErrors({ ...errors, web: "Invalid URL" });

    editProfile();
  };

  const editProfile = () => {
    setErrors({});
    if (
      hasFirstNameChanged ||
      hasLastNameChanged ||
      hasProfessionChanged ||
      hasBioChanged ||
      hasWebChanged ||
      hasImageChanged ||
      !hasSocialLinksChanged
    ) {
      const payload = {
        first_name: firstName,
        last_name: lastName,
        user_profile: {
          social_media_links: convertSocialLinks(profileSocialMediaLinks)
            .social_media_links,
          profile_image: profileImg,
          bio: description,
          profession: occupation,
          website_url: web,
        },
      };

      setIsLoading(true);
      userEdit.mutate(payload, {
        onSuccess: (response) => {
          console.log("Edit profile Success: ", response);
          dispatch(editUserProfileAction(response?.data));
          onBackClick();
          setIsLoading(false);
        },
      });
    } else {
      onBackClick();
    }
  };

  const renderLinkFields = () => {
    return (
      <AddLinksForm
        areLinksValid={areLinksValid}
        setAreLinksValid={setAreLinksValid}
        setSocialMediaLinks={(e) => setProfileSocialMediaLinks(e)}
        socialLinks={convertToSocialLinks(userProfile.social_media_links)}
      />
    );
  };

  const [showImageCropper, setShowImageCropper] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    file && setUploadedFile(URL.createObjectURL(file));
    file && setShowImageCropper(true);
    e.target.value = "";
  };

  const callBackImageCropped = async (imageFile) => {
    setShowImageCropper(false);

    imageFile && setUploadedFile(URL.createObjectURL(imageFile));

    try {
      setLoading(true);
      const { location } = await handleFileUpload(imageFile);
      setProfileImg(location);
      setLoading(false);
    } catch (err) {
      console.error("File upload failed:", err);
      setLoading(false);
    }
  };

  const removeImage = () => {
    setProfileImg(ASSETS.userPlaceholder);
  };

  const callBackImageNotCropped = () => setShowImageCropper(false);
  const parentRef = useRef(null);

  const handleProfileClick = () => {
    fileInputRef.current.click();
    setEditImage(false);
  };

  const innerDivRef = useRef(null);

  const handleMouseEnter = () => {
    if (innerDivRef.current) {
      innerDivRef.current.style.opacity = 1;
    }
  };

  const handleMouseLeave = () => {
    if (innerDivRef.current) {
      innerDivRef.current.style.opacity = 0;
    }
  };
  const height = 40;
  return (
    <div style={{ margin: "auto" }}>
      {isLoading ? (
        <CircularProgress
          size={24}
          style={{
            marginTop: "15vh",
            marginBottom: "15vh",
            marginLeft: "40%",
          }}
        />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              marginBottom: 5,
            }}
          >
            <Typography
              variant="h6"
              style={{
                marginBottom: "8px",
                fontSize: "12px",
                fontFamily: "DM Sans",
                lineHeight: "20px",
              }}
            >
              Edit Profile
            </Typography>
            <CloseIcon
              onClick={onBackClick}
              style={{
                fontSize: "16px",
                color: "#123051",

                cursor: "pointer",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "8px",
            }}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: "none" }}
              ref={fileInputRef}
              name="imageInput"
              id="imageInput"
            />
            {showImageCropper && (
              <CropperProfilePhoto
                ImageURL={uploadedFile}
                callBackImageCropped={callBackImageCropped}
                callBackImageNotCropped={callBackImageNotCropped}
              />
            )}
            {loading ? (
              <>
                <div>
                  <div
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      borderRadius: "35px",
                      height: "70px",
                      width: "70px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      position: "absolute",
                      top: 43,
                    }}
                  >
                    <CircularProgress size={10} style={{ color: "white" }} />
                  </div>
                  <img
                    src={
                      profileImg !== "" ? profileImg : ASSETS.userPlaceholder
                    }
                    alt="profileImg"
                    style={{
                      height: "70px",
                      width: "70px",
                      cursor: "pointer",
                      borderRadius: "35px", // Note: use "px" with borderRadius
                      border: "0.5px solid #EEEEEE",
                      marginRight: "5px",
                    }}
                  />
                </div>
              </>
            ) : (
              <div
                style={{
                  cursor: "pointer",
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div
                  ref={innerDivRef}
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    borderRadius: "35px",
                    height: "70px",
                    width: "70px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    opacity: 0, // Show on hover
                    transition: "opacity 0.3s ease", // Smooth transition for opacity
                    position: "absolute",
                    top: 49,
                    //   cursor: "pointer",
                  }}
                  onClick={() => setEditImage(true)}
                >
                  <div
                    style={{
                      width: 25,
                      height: 25,
                      borderRadius: 15,
                      backgroundColor: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={ASSETS.edit} style={{ width: 15, height: 15 }} />
                  </div>
                </div>
                <img
                  src={profileImg !== "" ? profileImg : ASSETS.userPlaceholder}
                  alt="profileImg"
                  style={{
                    height: "70px",
                    width: "70px",
                    cursor: "pointer",
                    borderRadius: "35px", // Note: use "px" with borderRadius
                    border: "0.5px solid #EEEEEE",
                    marginRight: "5px",
                  }}
                />
                {editImage ? (
                  <div
                    onClick={() => setEditImage(false)}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 1,
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        backgroundColor: "white",
                        zIndex: 1,
                        left: 15,
                        marginTop: 5,
                        top: 120,
                        borderRadius: 8,
                        overflow: "hidden",
                        boxShadow: "1px 2px 9px #bbbbbb",
                      }}
                    >
                      <div
                        className="editDropdown"
                        onClick={handleProfileClick}
                      >
                        <img
                          src={ASSETS.upload}
                          style={{ marginRight: 8, height: 15, width: 15 }}
                        />
                        <Typography style={{ fontSize: 12 }}>
                          Upload New
                        </Typography>
                      </div>
                      {profileImg !== "" ? (
                        <div
                          className="editDropdown"
                          onClick={removeImage}
                          style={{ marginTop: 5 }}
                        >
                          <img
                            src={ASSETS.delete_icon}
                            style={{ marginRight: 8, height: 15, width: 15 }}
                          />
                          <Typography style={{ fontSize: 12 }}>
                            Remove
                          </Typography>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
            <div>
              <Stack display={"flex"} flexDirection={"row"}>
                <TextField
                  variant="filled"
                  placeholder="Type your first name"
                  size="small"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  style={{
                    marginBottom: "8px",
                    background: "white",
                    marginRight: "2px",
                  }}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      fontSize: "8px",
                      height: "30px",
                      alignItems: "center",
                      // marginLeft: "10px",
                      borderRadius: "6px",
                      backgroundColor: "#F6F7F9",
                      // color: "#BBBBBB",
                      fontFamily: "DM Sans",
                    },
                    inputProps: {
                      style: {
                        fontSize: "8px",
                        paddingLeft: "9px",
                        paddingTop: "10px",
                      },
                    },
                  }}
                />
                <TextField
                  variant="filled"
                  placeholder="Type your last name"
                  size="small"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  style={{
                    marginBottom: "8px",
                    background: "white",
                  }}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      fontSize: "8px",
                      height: "30px",
                      alignItems: "center",
                      // marginLeft: "10px",
                      borderRadius: "6px",
                      backgroundColor: "#F6F7F9",
                      // color: "#BBBBBB",
                      fontFamily: "DM Sans",
                    },
                    inputProps: {
                      style: {
                        fontSize: "8px",
                        paddingLeft: "9px",
                        paddingTop: "10px",
                      },
                    },
                  }}
                />
              </Stack>
              {errors.name ? (
                <Typography
                  style={{
                    fontSize: 8,
                    color: "red",
                    marginLeft: 5,
                    marginTop: -5,
                    marginBottom: 5,
                  }}
                >
                  {`*${errors.name}`}
                </Typography>
              ) : null}
              <TextField
                variant="filled"
                placeholder="Type your occupation"
                fullWidth
                size="small"
                value={occupation}
                onChange={(e) => setOccupation(e.target.value)}
                style={{
                  marginBottom: "6px",
                  background: "white",
                }}
                InputProps={{
                  disableUnderline: true,
                  style: {
                    fontSize: "8px",
                    height: "30px",
                    alignItems: "center",

                    borderRadius: "6px",
                    backgroundColor: "#F6F7F9",
                    fontFamily: "DM Sans",
                  },
                  inputProps: {
                    style: {
                      fontSize: "8px",
                      paddingLeft: "9px",
                      paddingTop: "10px",
                    },
                  },
                }}
              />
            </div>
          </div>

          <TextField
            variant="filled"
            placeholder="Description here"
            fullWidth
            size="small"
            value={description}
            multiline
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
            style={{ marginBottom: "6px", background: "white" }}
            InputProps={{
              disableUnderline: true,
              style: {
                fontSize: "8px",
                height: "70px",
                borderRadius: "6px",
                backgroundColor: "#F6F7F9",
                // color: "#BBBBBB",
                fontFamily: "DM Sans",
              },
              inputProps: {
                style: {
                  fontSize: "8px",
                  paddingLeft: "2px",
                  paddingBottom: "35px",
                },
              },
            }}
            InputLabelProps={{
              shrink: true,
              style: {
                fontSize: "8px",
                top: "0px",
                left: "9px",
              },
            }}
          />

          {renderLinkFields()}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 10,
            }}
          >
            <Button
              style={{
                borderRadius: "20px",
                fontSize: "8px",
                height: "19px",
                backgroundColor: "#F6F7F9",
                color: "black",
                width: "47%",
              }}
              onClick={onBackClick}
            >
              Cancel
            </Button>
            <Button
              disabled={!areLinksValid}
              style={{
                borderRadius: "20px",
                fontSize: "8px",
                height: "19px",
                width: "47%",
              }}
              onClick={validateData}
            >
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default EditProfileCard;
